/**
 * The queries a rival outranks you on, and the section they do it with.
 *
 * This is the fourth kind of finding the Supporting pages card can hold, and
 * the review surface was dropping it — which produced a card carrying a
 * GAP FOUND badge over the words "Nothing on this topic that they have and you
 * don't", pre-ticked to send. The badge was right and the body was incomplete:
 * `hasSupportingPagesActionableGap` counts these, and nothing rendered them.
 *
 * It is arguably the most useful of the four. A supporting-page gap is a page
 * you have not written; this is a page you HAVE written being beaten, with the
 * beating section named — on the pilot, "They rank #3 for 'obsidian vs notion'
 * (3,600/mo) — you rank #19."
 *
 * The wording is not composed here. `evidenceLine` and `focalCoverageLine` are
 * built where the comparison is done, and are rendered verbatim, so the review
 * and the deep-dive cannot describe the same gap differently.
 */

/**
 * Rank-driven gaps are OFF, and this is the switch.
 *
 * The card's design is sound — "what does the rival rank for that we don't, and
 * which of their sections earns it, so we can write that section". What it
 * cannot currently do is tell a genuinely adjacent query from a re-wording of
 * the page's own. Measured on the pilot's 142 stored rows:
 *
 * - **56** were the page's own keyword outright.
 * - **~57 more** were a subset or superset of it ("delivery model" under
 *   "delivery service model"; "what is a service delivery" under the same).
 * - **37%** pointed at the rival's H1 — their page title, not a section.
 * - On **48%** of pages with more than one row, every row pointed at the SAME
 *   paragraph of the focal page: one thing to deepen, reported twice.
 * - Genuinely novel keywords, counted after every filter: **4 of 142**.
 *
 * Three separate lexical rules were tried and each failed in a way that is
 * recorded in TECHNICAL_DEBT §0ag. The last one is the clearest: tightening to
 * "drop subsets of the page keyword" cuts "microsoft whiteboard" (14,800/mo, a
 * genuinely different query) while keeping "delete powerpoint slide" against a
 * page targeting "PowerPoint slide deletion" — the same query. Word overlap
 * cannot separate these, and no amount of tuning will make it.
 *
 * So the surface is off rather than half-right: paying misleading rows on 45
 * pages for a 3% hit rate is a bad trade, and Supporting pages and Missing
 * sections — 90% of the cards, 1,664 rows — are untouched by this.
 *
 * **To turn it back on**, all of these must be true:
 *
 * 1. Relevance is judged semantically, not lexically. The workspace already
 *    holds the material: `topicembeddings` and the plan-topic clusters.
 * 2. Rows are deduplicated by the FOCAL section they point at, not only by the
 *    rival section they came from.
 * 3. Page-title headings stay excluded — `isPageTitleHeading` already does this
 *    for section gaps and must cover this path too.
 *
 * Flip this constant and the card returns. Nothing else is stubbed out, so the
 * rest of the pipeline keeps being exercised by its own tests meanwhile.
 */
export const RANK_DRIVEN_GAPS_ENABLED = false;

export interface RankDrivenGapLike {
  keyword?: string;
  evidenceLine?: string;
  variantKeywords?: string[];
  groupedKeywords?: string[];
  groupedSearchVolume?: number;
  sectionHeading?: string;
  sectionCovered?: boolean;
  focalCoverageLine?: string | null;
  searchVolume?: number | null;
  priorityScore?: number | null;
}

export interface ReviewRankGapRow {
  keyword: string;
  /** "They rank #3 for X (3,600/mo) — you rank #19." Rendered as given. */
  evidence: string;
  /** The rival's section that does the work. */
  section: string;
  /** How thin your version is, when the comparison could say. */
  coverage: string | null;
  /**
   * Word-order variants folded into this row, already phrased.
   *
   * Null when there are none. Shown rather than dropped so an operator can see
   * that "delivery service model" and "models of service delivery" were the
   * same 260/mo query, instead of wondering where two findings went.
   */
  variants: string | null;
  /**
   * The other queries merged onto this section, or null.
   *
   * Deliberately makes no ranking claim — `evidence` owns that, and owns the
   * difference between "you rank #24" and "you do not rank".
   */
  demand: string | null;
  /**
   * True when your page has no equivalent section — something to WRITE.
   *
   * False means you cover it and it is thin, which is a different job and a
   * different word for the card above it.
   */
  isMissing: boolean;
}

export interface ReviewRankGapsView {
  /** The whole sentence, for anywhere that cannot render the link. */
  verdict: string;
  /** The rival's name — the half of the sentence that becomes the link. */
  rivalLabel: string;
  /** The rest of the sentence, so the link stops at the name. */
  verdictRest: string;
  /**
   * The rival PAGE that outranks you, never a bare domain.
   *
   * Same reasoning as the section-gap card: an operator clicking a rival's
   * name here is asking to read the page beating them, and a homepage makes
   * them hunt for it.
   */
  rivalUrl: string | null;
  rows: ReviewRankGapRow[];
}

/**
 * Rows in the order the comparison ranked them, not re-sorted here.
 *
 * A gap with no evidence line is dropped rather than rendered blank: the
 * evidence IS the finding on this card, and a row that cannot say why it is a
 * gap is a row an operator cannot act on.
 */
/**
 * The OTHER keywords pointing at this section. Never the row's own.
 *
 * This replaced a line that restated `evidenceLine` and got it wrong: it
 * asserted "you don't" while the line directly beneath said "you rank #24".
 * `evidenceLine` already carries the ranking claim and is the only thing that
 * should — it knows the difference between ranking badly and not ranking, and
 * this did not.
 *
 * So this line makes no ranking claim and repeats no volume. It answers one
 * question: what else did this section get merged for?
 */
function buildAlsoRanksLine(gap: RankDrivenGapLike): string | null {
  const extra = (gap.groupedKeywords || []).map((k) => (k || '').trim()).filter(Boolean);
  if (!extra.length) return null;

  const shown = extra.slice(0, 4);
  const more = extra.length - shown.length;
  return `Also ranks for: ${shown.join(', ')}${more > 0 ? ` (+${more} more)` : ''}`;
}

/**
 * The gate. Everything the app calls goes through here.
 *
 * Composition lives in `composeReviewRankGaps` below and stays fully tested
 * while the surface is off — a switched-off feature whose logic stops being
 * exercised is a feature that quietly rots until the day someone flips it back
 * on and finds out.
 */
export function reviewRankGaps(
  gaps: readonly RankDrivenGapLike[] | null | undefined,
  rivalDomain: string | null | undefined,
  rivalUrl?: string | null
): ReviewRankGapsView | null {
  if (!RANK_DRIVEN_GAPS_ENABLED) return null;
  return composeReviewRankGaps(gaps, rivalDomain, rivalUrl);
}

export function composeReviewRankGaps(
  gaps: readonly RankDrivenGapLike[] | null | undefined,
  rivalDomain: string | null | undefined,
  rivalUrl?: string | null
): ReviewRankGapsView | null {
  const rows: ReviewRankGapRow[] = [];

  for (const gap of gaps ?? []) {
    const evidence = (gap?.evidenceLine || '').trim();
    if (!evidence) continue;
    const variants = (gap.variantKeywords || [])
      .map((v) => (v || '').trim())
      .filter(Boolean);
    rows.push({
      keyword: (gap.keyword || '').trim(),
      evidence,
      section: (gap.sectionHeading || '').trim(),
      coverage: (gap.focalCoverageLine || '').trim() || null,
      variants: variants.length
        ? `Same query, also written: ${variants.join(', ')}`
        : null,
      demand: buildAlsoRanksLine(gap),
      isMissing: gap.sectionCovered !== true
    });
  }

  if (!rows.length) return null;

  // Named when we know who, because "a rival" is a worse sentence than
  // "slite.com" and the name is what an operator recognises.
  const who = (rivalDomain || '').trim();
  const subject = who && who !== 'top rival' ? who : 'A rival';
  const verdictRest =
    rows.length === 1
      ? ' outranks you on 1 query where you already have the page.'
      : ` outranks you on ${rows.length} queries where you already have the page.`;

  const href = (rivalUrl || '').trim();
  const linkable = /^https?:\/\//i.test(href) && !/^https?:\/\/[^/]+\/?$/i.test(href);

  return {
    verdict: `${subject}${verdictRest}`,
    rivalLabel: subject,
    verdictRest,
    rivalUrl: linkable ? href : null,
    rows
  };
}
